import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { ArrowRight, Loader2, Search, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { resolvePostImage } from "@/lib/postImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LeafSignature from "@/components/heritage/LeafSignature";

type ArchivePost = {
  id: string;
  title: string;
  slug: string;
  image_url: string | null;
  image_alt: string | null;
  discover_category: string | null;
  summary: string | null;
  published_at: string;
};

const PAGE_SIZE = 6;

const CATEGORIES: { value: string; label: string }[] = [
  { value: "all", label: "All Entries" },
  { value: "taste-of-st-kitts", label: "Taste" },
  { value: "history-heritage", label: "Heritage" },
  { value: "nature-geography", label: "Nature" },
  { value: "festivals-traditions", label: "Festivals" },
  { value: "experience-it-like-a-local", label: "Local Life" },
  { value: "curious-visitor-faq", label: "Visitor FAQ" },
  { value: "__uncategorized__", label: "Field Notes" },
];

const formatCategory = (cat: string | null) =>
  (cat || "Field Notes").replace(/-/g, " ");

const SkeletonCard = () => (
  <div className="animate-pulse">
    <div className="aspect-[4/5] rounded-sm mb-6 bg-[hsl(150_35%_14%)]" />
    <div className="h-3 w-24 bg-[hsl(150_35%_14%)] rounded mb-3" />
    <div className="h-5 w-5/6 bg-[hsl(150_35%_14%)] rounded mb-2" />
    <div className="h-5 w-3/5 bg-[hsl(150_35%_14%)] rounded mb-4" />
    <div className="h-3 w-full bg-[hsl(150_35%_14%)] rounded mb-1.5" />
    <div className="h-3 w-4/5 bg-[hsl(150_35%_14%)] rounded" />
  </div>
);

const FromTheArchive = () => {
  const [posts, setPosts] = useState<ArchivePost[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const buildQuery = useCallback(
    (from: number, to: number) => {
      let q = supabase
        .from("blog_posts")
        .select(
          "id,title,slug,image_url,image_alt,discover_category,summary,published_at"
        )
        .order("published_at", { ascending: false })
        .range(from, to);

      if (category === "__uncategorized__") {
        q = q.is("discover_category", null);
      } else if (category !== "all") {
        q = q.eq("discover_category", category);
      }

      if (search) {
        const escaped = search.replace(/[%,()]/g, " ");
        q = q.or(
          `title.ilike.%${escaped}%,summary.ilike.%${escaped}%`
        );
      }
      return q;
    },
    [category, search]
  );

  const fetchPage = useCallback(
    async (nextPage: number) => {
      const from = nextPage * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, error: err } = await buildQuery(from, to);
      if (err) throw err;
      const rows = (data as ArchivePost[]) || [];
      setHasMore(rows.length === PAGE_SIZE);
      return rows;
    },
    [buildQuery]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPage(0)
      .then((rows) => {
        if (cancelled) return;
        setPosts(rows);
        setPage(0);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const rows = await fetchPage(next);
      setPosts((prev) => [...prev, ...rows]);
      setPage(next);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const skeletons = useMemo(
    () => Array.from({ length: PAGE_SIZE }, (_, i) => i),
    []
  );

  return (
    <section
      className="relative overflow-hidden py-24 md:py-32"
      style={{
        background:
          "linear-gradient(180deg, hsl(25 25% 12%) 0%, hsl(150 32% 10%) 100%)",
      }}
    >
      <LeafSignature variant="watermark" position="top-right" size="xl" tone="dark" />
      <LeafSignature variant="watermark" position="bottom-left" size="lg" tone="dark" />
      <div className="relative container mx-auto px-6" style={{ maxWidth: "1400px" }}>
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12 md:mb-14">
          <div className="max-w-2xl">
            <p className="font-sans text-[11px] tracking-[0.42em] uppercase text-accent mb-5">
              Cultural Archive
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-[hsl(40_33%_96%)] leading-[1.05] mb-6">
              From the Archive
            </h2>
            <p className="font-sans text-base md:text-lg text-white leading-relaxed">
              Island history, folklore, food memory and the quiet manufacturing
              past of St. Kitts — a Caribbean cultural journal in slow motion.
            </p>
          </div>
          <div className="flex flex-col gap-3 self-start lg:self-end lg:items-end">
            <Link
              to="/blog"
              className="group inline-flex items-center gap-3 font-sans text-[11px] tracking-[0.32em] uppercase text-[hsl(40_33%_96%)] hover:text-accent transition-colors"
            >
              Open the Archive
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/archive"
              className="group inline-flex items-center gap-3 font-sans text-[11px] tracking-[0.32em] uppercase text-accent hover:text-[hsl(40_33%_96%)] transition-colors"
            >
              The Living Archive of St. Kitts &amp; Nevis
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Reveal>

        {/* Filters + search */}
        <Reveal className="mb-12 md:mb-16 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const active = category === c.value;
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCategory(c.value)}
                  className={`font-sans text-[10px] tracking-[0.28em] uppercase px-4 py-2 rounded-full border transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(150_32%_10%)] ${
                    active
                      ? "bg-accent text-accent-foreground border-accent shadow-[0_0_0_1px_hsl(var(--accent))]"
                      : "border-[hsl(40_33%_94%)]/40 text-[hsl(40_33%_96%)] hover:text-[hsl(40_33%_98%)] hover:border-accent hover:bg-accent/10"
                  }`}
                  aria-pressed={active}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          <div className="relative w-full lg:w-72 shrink-0">
            <Search
              className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(40_33%_94%)]/55"
              aria-hidden
            />
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search the archive…"
              aria-label="Search the archive"
              className="pl-9 pr-9 bg-[hsl(150_35%_8%)]/60 border-[hsl(40_33%_94%)]/20 text-[hsl(40_33%_96%)] placeholder:text-[hsl(40_33%_94%)]/45 focus-visible:ring-accent focus-visible:border-accent"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[hsl(40_33%_94%)]/55 hover:text-accent"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </Reveal>

        {error ? (
          <p className="text-center text-[hsl(40_33%_94%)]/65 font-sans py-12">
            The archive is resting. Please try again shortly.
          </p>
        ) : loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16 md:gap-x-14 md:gap-y-20">
            {skeletons.map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <p className="text-center text-[hsl(40_33%_94%)]/65 font-sans py-16">
            No entries match this filter yet. Try another category or search term.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16 md:gap-x-14 md:gap-y-20">
              {posts.map((p, idx) => {
                const image = resolvePostImage(p.image_url, p.discover_category);
                return (
                  <Reveal key={p.id} delay={Math.min(idx, 5) * 70}>
                    <Link to={`/blog/${p.slug}`} className="group block">
                      <div className="relative aspect-[4/5] overflow-hidden rounded-sm mb-7 md:mb-8 bg-[hsl(150_35%_8%)]">
                        <img
                          src={image}
                          alt={p.image_alt || p.title}
                          loading="lazy"
                          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.05] transition-all duration-[1100ms] ease-out"
                        />
                        <div
                          aria-hidden
                          className="absolute inset-0 bg-gradient-to-t from-[hsl(150_35%_8%)]/60 via-transparent to-transparent"
                        />
                      </div>
                      <p className="font-sans text-[10px] uppercase tracking-[0.32em] text-accent mb-3 md:mb-4 capitalize">
                        {formatCategory(p.discover_category)}
                      </p>
                      <h3 className="font-serif text-[1.35rem] md:text-2xl text-[hsl(40_33%_96%)] leading-[1.25] mb-3 md:mb-4 group-hover:text-accent transition-colors duration-500">
                        {p.title}
                      </h3>
                      {p.summary && (
                        <p className="font-sans text-[13.5px] md:text-[14px] text-white leading-[1.7] line-clamp-2">
                          {p.summary}
                        </p>
                      )}
                    </Link>
                  </Reveal>
                );
              })}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-16 md:mt-20">
                <Button
                  onClick={loadMore}
                  disabled={loadingMore}
                  variant="outline"
                  size="lg"
                  className="font-sans uppercase tracking-[0.28em] text-[11px] px-10 border-[hsl(40_33%_94%)]/30 bg-transparent text-white hover:bg-accent hover:text-accent-foreground hover:border-accent"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading
                    </>
                  ) : (
                    <>Load More Entries</>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default FromTheArchive;
